import type { Product } from '@/types';
import { sizeStockTotal } from '@/lib/cart/sizes';
import { isIndexableProduct } from '@/lib/seo/pages';

type AvailabilityFields = Pick<Product, 'status' | 'stockQuantity' | 'checkoutMode' | 'metadata'>;

/**
 * Nothing left to sell: not on pre-order (reasortimiento never sells out) and
 * either no units on hand or marked `sold_out` by hand (even with stock, since
 * the checkout only sells `active` pieces). A sized product counts as a whole:
 * its total, also capped by the sum of `metadata.sizeStock` when tracked per
 * size, so every size at 0 is sold out. Shared (PDP, prerender, Worker): pure.
 */
export function isSoldOut(p: AvailabilityFields): boolean {
  if (p.metadata.preorder === true) return false;
  if (p.status === 'sold_out') return true;
  const perSize = sizeStockTotal(p);
  const units = perSize === null ? p.stockQuantity : Math.min(p.stockQuantity, perSize);
  return units <= 0;
}

/**
 * Whether the storefront presents the piece as sold out (PDP buy block, card
 * label, end of the listing): sold out AND bought directly. An enquiry piece
 * keeps its «solicitar información» path whatever its stock.
 */
export function showsSoldOut(p: AvailabilityFields): boolean {
  return isSoldOut(p) && p.checkoutMode !== 'enquiry';
}

/** A one-off piece (`metadata.uniquePiece`) already sold: shown as «Pieza vendida», no restock alert. */
export function isSoldPiece(p: AvailabilityFields): boolean {
  return isSoldOut(p) && p.metadata.uniquePiece === true;
}

/**
 * Whether a «avísame cuando vuelva» alert may be created: sold out, not a
 * unique piece (it will not come back), bought directly (enquiry pieces are
 * asked about instead) and on an indexable page (not draft / archived).
 */
export function acceptsRestockAlert(p: AvailabilityFields): boolean {
  return (
    isSoldOut(p) &&
    p.metadata.uniquePiece !== true &&
    p.checkoutMode !== 'enquiry' &&
    isIndexableProduct(p)
  );
}

/**
 * `metadata.publishAt` as written by /admin: ISO 8601 date-time WITH an explicit
 * offset (or Z), minutes required, seconds optional («2026-11-05T19:00:00+01:00»).
 * A bare local time would be read in the runtime's zone (UTC on the Worker), so
 * it is refused. Shared by the admin Zod schema and the tests. Pure.
 */
const PUBLISH_AT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/;
export function isValidPublishAt(value: string): boolean {
  return PUBLISH_AT_RE.test(value) && Number.isFinite(Date.parse(value));
}

/**
 * The instant (ms) a product goes live: `metadata.publishAt` parsed, or null
 * when it has none (always live). An unparseable value yields NaN, which
 * `isLive` treats as never (like `publishInstant` for Edit articles).
 */
export function productPublishInstant(p: Pick<Product, 'metadata'>): number | null {
  const at = p.metadata.publishAt;
  if (typeof at !== 'string' || at.trim() === '') return null;
  return Date.parse(at);
}

/**
 * Whether the product is public at `now` (ms): no `publishAt` = live; a
 * scheduled one only from that exact instant on (same semantics as
 * `isPublished` in lib/articles). THE one gate of a scheduled drop: listing
 * (`isListed`), GET /api/products/:slug and the Worker PDP (404), sitemap,
 * llms.txt, «Selección CARO» and the server cart (`priceCart`). Independent of
 * `status` (a draft stays hidden by its own rule). Pure: `now` is explicit.
 */
export function isLive(p: Pick<Product, 'metadata'>, now: number): boolean {
  const at = productPublishInstant(p);
  return at === null || (Number.isFinite(at) && at <= now);
}
